<template>
    <div class="bg-gray-100 rounded-lg px-4 py-2">

        <div v-if="!text" @click="startEditing" class="text-gray-500 text-sm italic flex items-center">
            <div class="flex-1">Tap to add a description</div>
            <i class="icon-edit text-blue-500"></i>
        </div>
        <div v-else>
            <div v-if="createdBy && createdAt" class="text-xs text-gray-500 font-medium flex items-center">
                <span class="truncate">{{ createdBy }}</span>
                <span class="mx-2">•</span>
                <span class="text-nowrap">{{ formatTimeAgo(createdAt) }}</span>
            </div>
            <div class="text-gray-700 font-medium text-sm flex items-center">
                <div class="flex-1">{{ text }}</div>
                <IconButton @click="startEditing" icon="icon-edit" size="small"
                    class="relative -right-2 top-1" />
            </div>
        </div>
    </div>

    <IonModal ref="modal">
        <IonHeader>
            <IonToolbar>
                <IonTitle>Editing Description</IonTitle>
                <IonButtons slot="end">
                    <IconButton @click="cancelClicked" icon="icon-x"/>
                </IonButtons>
            </IonToolbar>
        </IonHeader>
        <IonContent fullscreen :scroll-x="false">
            <div class="p-4 h-full">
                <IonTextarea ref="textarea" class="font-medium text-gray-700 h-full" v-model="editingText">
                </IonTextarea>
            </div>
        </IonContent>
        <IonFooter class="ion-no-border">
            <IonToolbar>
                <div class="grid grid-cols-2 space-x-3">
                    <LightBlockButton :disabled="isSaving" @click="cancelClicked">
                        <i class="icon-x mr-1 text-lg text-gray-700"></i><span class="text-gray-700">Cancel</span>
                    </LightBlockButton>
                    <PrimaryBlockButton :disabled="isSaving || !canSave" @click="saveClicked">
                        <i v-if="!isSaving" class="icon-check mr-1 text-lg"></i>
                        {{ isSaving ? 'Saving' : 'Save' }}
                        <IonSpinner v-if="isSaving" class="absolute right-0" name="dots" color="white"></IonSpinner>
                    </PrimaryBlockButton>
                </div>
            </IonToolbar>
        </IonFooter>
    </IonModal>
</template>

<script lang="ts" setup>
import IconButton from '@/components/forms/IconButton.vue';
import LightBlockButton from '@/components/forms/LightBlockButton.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import { useToast } from '@/composables/use-toast';
import {
    IonSpinner,
    IonTextarea,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonFooter,
    IonButtons,
} from '@ionic/vue';
import { formatTimeAgo } from '@vueuse/core';
import { computed, ref } from 'vue';

const { errorToast } = useToast();

const props = defineProps<{
    text?: string;
    createdBy?: string;
    createdAt?: Date;
    onSave: (description: string) => Promise<{ success: boolean }>;
}>();

const modal = ref<InstanceType<typeof IonModal>>();
const isSaving = ref(false);
const editingText = ref('');
const textarea = ref();
const canSave = computed(() => editingText.value !== props.text);

function startEditing() {
    editingText.value = props.text || '';
    modal.value?.$el.present();
}

function cancelClicked() {
    modal.value?.$el.dismiss();
}

async function saveClicked() {
    if (editingText.value === props.text) {
        return;
    }
    isSaving.value = true;
    const { success } = await props.onSave(editingText.value)
    isSaving.value = false;
    if (!success) {
        errorToast('Oops. Something went wrong while saving the description. Please try again.')
    }
    modal.value?.$el.dismiss();
}

</script>

<style scoped>
ion-footer {
    padding-left: 10px;
    padding-right: 10px;
    --background: white;
    background: white;
}

ion-footer ion-toolbar {
    @apply border-t-2 border-gray-300;
    --background: white;
}
</style>